import { useIncidentStore } from '@/store/incident.store';
import FaIcon from '@/components/common/FaIcon';

interface ExerciseBannerBarProps {
  /** Incident the drill warning refers to — always name it, see below. */
  incidentNumber?: string;
  incidentLabel?: string;
}

/**
 * Presentational drill bar. Extracted so the store-driven banner (engaged
 * incident) and the record-driven one on an incident's detail page cannot
 * drift into two different-looking warnings.
 *
 * The qualifier is deliberately NOT `hidden sm:inline`: on a phone the banner
 * would otherwise read only "Exercise — not a real event" with no referent,
 * which is ambiguous — and dangerously so when a drill is engaged while the
 * operator is looking at a REAL incident's page.
 */
export function ExerciseBannerBar({ incidentNumber, incidentLabel }: ExerciseBannerBarProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex flex-wrap items-center justify-center gap-x-2 border-b border-[color:var(--severity-warning-spine)]/40 bg-[color:var(--severity-warning-bg)] px-4 py-1.5 text-center text-sm font-semibold text-[color:var(--severity-warning-text)]"
    >
      <FaIcon icon="triangle-exclamation" className="h-4 w-4 shrink-0" />
      <span className="tracking-wide uppercase">Exercise — not a real event</span>
      {(incidentNumber || incidentLabel) && (
        <span className="font-normal normal-case opacity-80">
          · {incidentNumber} {incidentLabel}
        </span>
      )}
    </div>
  );
}

/**
 * Persistent, unmissable banner shown whenever the engaged incident is a
 * sandboxed exercise (is_exercise = true). This is the UI half of the
 * Exercise Mode safety invariant: it must be obvious the operator is drilling,
 * not responding to a real event. Amber styling stays within the color budget
 * (reuses the warning severity tokens).
 *
 * NOTE: this one keys off the ENGAGED incident, so it says nothing about the
 * record currently on screen. Drill marking for a displayed incident must be
 * derived from that record — see IncidentHeader.
 */
export default function ExerciseBanner() {
  const activeIncident = useIncidentStore((s) => s.activeIncident);

  if (!activeIncident?.is_exercise) return null;

  return (
    <ExerciseBannerBar
      incidentNumber={activeIncident.incident_number}
      incidentLabel={activeIncident.short_name || activeIncident.name}
    />
  );
}
